//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { yupResolver } from '@hookform/resolvers/yup'
import { Backdrop, Box, Button, CircularProgress, Grid, useTheme } from '@mui/material'
import EditField from '@react/components/elements/EditField'
import React from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { EthAddr, ethDateToString, stringToEthDate, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import { numberToApr } from '@shared/conversions'
import { PoolOverviewCTO } from '@shared/sharedTypes'
import * as yup from 'yup'
import { InferType } from 'yup'

import { onPromise } from '@react/components/UiHelpers'
import ButtonAction from '@react/components/elements/ButtonAction'
import { useOtChainState, useOtChainTimestamp, useOtDispatch } from '@react/redux/store'
import { createLoanTx } from '@react/signedTxs/createLoanTx'
import { logOT } from '@shared/loggingShared'

export const LoanCreateFormSchema = yup.object({
  accrualStartTimestampEthDate: yup
    .string()
    .required()
    .label('accrualStartTimestamp')
    .meta(toFieldMetaData({ propName: 'accrualStartTimestampEthDate', stringType: StringTypeEnum.ETH_DATE })),
  transferInWindowDuration: yup
    .number()
    .label('transferInWindowDuration')
    .meta(
      toFieldMetaData({
        propName: 'transferInWindowDuration',
        stringType: StringTypeEnum.TIME_INTERVAL_DAYS,
      })
    ),
  transferOutWindowDuration: yup
    .number()
    .label('transferOutWindowDuration')
    .meta(
      toFieldMetaData({
        propName: 'transferOutWindowDuration',
        stringType: StringTypeEnum.TIME_INTERVAL_DAYS,
      })
    ),
  originationBps: yup
    .number()
    .required()
    .default(100)
    .label('originationBps')
    .meta(toFieldMetaData({ propName: 'originationBps' })),
  tenor: yup
    .number()
    .required()
    .default(30)
    .label('tenor')
    .meta(toFieldMetaData({ propName: 'tenor' })),
  apr: yup
    .number()
    .required()
    .label('apr')
    .meta(toFieldMetaData({ propName: 'apr' })),
})

export interface LoanCreateFormData extends InferType<typeof LoanCreateFormSchema> {}

export default function LoanCreateForm(props: {
  poolAddr: EthAddr
  poolOverviewCTO: PoolOverviewCTO
}): React.ReactElement {
  const { poolAddr, poolOverviewCTO } = props

  const dispatch = useOtDispatch()
  const theme = useTheme()

  const {
    userConnection: { accountAddr },
  } = useOtChainState()

  const chainTimestamp = useOtChainTimestamp()

  logOT.debug('LoanCreateForm borrowerAddr ', accountAddr)
  const ONE_DAY = 3600 * 24

  const initialValues: LoanCreateFormData = {
    tenor: poolOverviewCTO.activeLoanState.durationDays > 0 ? poolOverviewCTO.activeLoanState.durationDays : 28,
    apr: Number(poolOverviewCTO.activeLoanState.finalizedApr) / 100,
    transferInWindowDuration: 2,
    transferOutWindowDuration: 2,
    originationBps: 100,
    accrualStartTimestampEthDate:
      poolOverviewCTO.activeLoanState.durationDays > 0
        ? ethDateToString(
            poolOverviewCTO.activeLoanState.accrualStartTimestamp +
              poolOverviewCTO.activeLoanState.durationDays * ONE_DAY
          )
        : ethDateToString(chainTimestamp + 4 * ONE_DAY),
  }
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoanCreateFormData>({
    resolver: yupResolver(LoanCreateFormSchema),
    defaultValues: initialValues,
  })
  // const [addInvestment, { isLoading: isAdding }] = useAddInvestmentMutation()
  // const account = {}
  const isLoading = false
  const handleClose = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const localOnSubmit: SubmitHandler<LoanCreateFormData> = async (data: LoanCreateFormData) => {
    logOT.debug('LoanCreate localOnSubmit')
    logOT.debug('localOnSubmit LoanCreateForm', data)
    try {
      logOT.debug('Data to send', data)

      const tenor = data.tenor

      const indicativeApr = numberToApr(data.apr)
      const originationBps = Number(data.originationBps)
      const transferInWindowDurationDays = Number(data.transferInWindowDuration)
      const transferOutWindowDurationDays = Number(data.transferOutWindowDuration)
      const accrualStartDayTimestamp = stringToEthDate(data.accrualStartTimestampEthDate)

      const priorLoanAddr = poolOverviewCTO.activeLoanState.loanAddr

      logOT.info(
        'Create Loan1',
        accountAddr,
        poolAddr,
        tenor,
        indicativeApr,
        transferInWindowDurationDays,
        priorLoanAddr
      )
      if (accountAddr && poolAddr) {
        logOT.info('DISPATCH createLoan', data.accrualStartTimestampEthDate, {
          poolAddr,
          accrualStartDayTimestamp,
          transferInWindowDurationDays,
          indicativeApr,
          tenor,
          originationBps,
          priorLoanAddr,
        })
        dispatch(
          createLoanTx({
            poolAddr,
            accrualStartDayTimestamp,
            transferInWindowDurationDays,
            transferOutWindowDurationDays,
            indicativeApr,
            finalizedApr: indicativeApr,
            tenor,
            originationBps,
            priorLoanAddr,
          })
        )
        logOT.debug('Create Loan4')
      }

      const event = new Event('modalClose')
      window.dispatchEvent(event)
    } catch (e) {
      console.error('poolCycle not found', e)
      throw new Error('poolCycle not created')
    }
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form onSubmit={onPromise(handleSubmit(localOnSubmit))}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box py={2}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={LoanCreateFormSchema.describe().fields.tenor}
            ></EditField>
          </Grid>
          <Grid item xs={12}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={LoanCreateFormSchema.describe().fields.originationBps}
            ></EditField>
          </Grid>

          <Grid item xs={12}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={LoanCreateFormSchema.describe().fields.transferInWindowDuration}
            ></EditField>
          </Grid>

          <Grid item xs={12}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={LoanCreateFormSchema.describe().fields.transferOutWindowDuration}
            ></EditField>
          </Grid>

          <Grid item xs={12}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              setValue={setValue}
              fieldSchema={LoanCreateFormSchema.describe().fields.accrualStartTimestampEthDate}
            ></EditField>
          </Grid>

          <Grid item xs={12}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={LoanCreateFormSchema.describe().fields.apr}
            ></EditField>
          </Grid>

          <Grid item xs={12}>
            <ButtonAction data-testid={'submit'} sx={{ marginRight: '16px' }} type="submit" variant={'primary'}>
              Create Loan
            </ButtonAction>
            <Button
              variant="outlined"
              sx={{
                color: theme.palette.fg.main,
                borderColor: theme.palette.fg.main,
                padding: '11px 16px 11px 16px',
                borderRadius: '6px',
              }}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Box>
    </form>
  )
}
